import type { PromotionCouponCreatePayload } from "@/modules/promotions/domain/types";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { FormRoot, FormInput, FormNumber, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";


import {
  InfoIcon,
  RefreshCwIcon,
  SparklesIcon,
  TicketIcon,
  ToggleLeftIcon,
} from "lucide-react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const CouponFormSchema = z.object({
  promotionId: z.string().min(1, "ກະລຸນາເລືອກໂປຣໂມຊັນ"),
  code: z.string().min(1, "ກະລຸນາໃສ່ລະຫັດຄູປອງ"),
  maxRedemptions: z.number().int().min(0).nullable().optional(),
  perCustomerLimit: z.number().int().min(0).nullable().optional(),
  isActive: z.boolean(),
});

export type CouponFormValues = z.infer<typeof CouponFormSchema>;

type CouponFormProps = {
  defaultValues?: Partial<CouponFormValues>;
  onSubmit: (values: PromotionCouponCreatePayload) => void | Promise<void>;
  submitting?: boolean;
  onCancel: () => void;
  submitLabel: string;
};

function generateCouponCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "";
  for (let i = 0; i < 8; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

export function CouponForm({
  defaultValues,
  onSubmit,
  submitting,
  onCancel,
  submitLabel,
}: CouponFormProps) {
  const methods = useForm<CouponFormValues>({
    resolver: zodResolver(CouponFormSchema),
    defaultValues: {
      promotionId: "",
      code: "",
      maxRedemptions: null,
      perCustomerLimit: null,
      isActive: true,
      ...defaultValues,
    },
  });

  const watchedValues = useWatch({
    control: methods.control,
  });

  const handleGenerateCode = () => {
    methods.setValue("code", generateCouponCode());
  };

  const handleSubmit = (values: CouponFormValues) => {
    onSubmit({
      promotionId: values.promotionId,
      code: values.code.trim().toUpperCase(),
      maxRedemptions: values.maxRedemptions ?? null,
      perCustomerLimit: values.perCustomerLimit ?? null,
      isActive: values.isActive,
    });
  };

  return (
    <FormRoot<CouponFormValues> methods={methods} onSubmit={handleSubmit}>
      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        {/* Left Column: Form */}
        <div className="space-y-6">
          {/* Basic Information */}
          <Card className="gap-2 rounded-xl border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <InfoIcon className="h-5 w-5 text-muted-foreground" />
                ຂໍ້ມູນພື້ນຖານ
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <RHF.Controller
                name="promotionId"
                control={methods.control}
                render={({ field }) => (
                  <div className="space-y-2">
                    <label
                      htmlFor="promotionId"
                      className="font-medium text-sm"
                    >
                      ແຄມເປນໂປຣໂມຊັນ <span className="text-destructive">*</span>
                    </label>
                    <InfiniteCombobox<{ id: string; name: string }>
                      queryKey={["promotions", "lookup", "active"]}
                      queryFn={(args) =>
                        fetchLookupForInfinite(
                          `${config.apiUrl}/loyalty/promotions/lookup`,
                          args,
                        )
                      }
                      preloadQueryFn={(id) =>
                        hydrateLookupItem(
                          `${config.apiUrl}/loyalty/promotions/lookup`,
                          id,
                        )
                      }
                      getLabel={(item) => item.name}
                      getValue={(item) => item.id}
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                      placeholder="ເລືອກແຄມເປນການຕະຫຼາດ"
                      className="w-full"
                    />
                    <p className="text-muted-foreground text-xs">
                      ເຊື່ອມຕໍ່ຄູປອງນີ້ກັບແຄມເປນທີ່ມີຢູ່ແລ້ວເພື່ອຕິດຕາມການວິເຄາະ.
                    </p>
                    {methods.formState.errors.promotionId && (
                      <p className="text-destructive text-xs">
                        {methods.formState.errors.promotionId.message}
                      </p>
                    )}
                  </div>
                )}
              />

              <div className="space-y-2">
                <label htmlFor="code" className="font-medium text-sm">
                  ລະຫັດຄູປອງ <span className="text-destructive">*</span>
                </label>
                <div className="flex gap-2">
                  <FormInput
                    name="code"
                    placeholder="ຕົວຢ່າງ: SUMMER2024"
                    className="flex-1"
                    disabled={submitting}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGenerateCode}
                    disabled={submitting}
                    className="shrink-0"
                  >
                    <SparklesIcon className="h-4 w-4" />
                    ສ້າງອັດຕະໂນມັດ
                  </Button>
                </div>
                {methods.formState.errors.code && (
                  <p className="text-destructive text-xs">
                    {methods.formState.errors.code.message}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Redemption Limits */}
          <Card className="gap-2 rounded-xl border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <RefreshCwIcon className="h-5 w-5 text-muted-foreground" />
                ຂີດຈຳກັດການແລກປ່ຽນ
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <FormNumber
                  name="maxRedemptions"
                  label="ຈຳນວນການແລກປ່ຽນສູງສຸດ"
                  placeholder="ບໍ່ຈຳກັດ"
                  min={0}
                  disabled={submitting}
                />
                <p className="text-muted-foreground text-xs">
                  ປ່ອຍໃຫ້ວ່າງເພື່ອການໃຊ້ງານບໍ່ຈຳກັດທົ່ວທັງລູກຄ້າ.
                </p>
              </div>
              <div className="space-y-2">
                <FormNumber
                  name="perCustomerLimit"
                  label="ຈຳກັດຕໍ່ລູກຄ້າ"
                  placeholder="ບໍ່ຈຳກັດ"
                  min={0}
                  disabled={submitting}
                />
                <p className="text-muted-foreground text-xs">
                  ລູກຄ້າຄົນໜຶ່ງສາມາດໃຊ້ລະຫັດນີ້ໄດ້ກີ່ຄັ້ງ.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Activation Status */}
          <Card className="gap-2 rounded-xl border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <ToggleLeftIcon className="h-5 w-5 text-muted-foreground" />
                ສະຖານະການເປີດໃຊ້ງານ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RHF.Controller
                name="isActive"
                control={methods.control}
                render={({ field }) => (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="isActive"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={submitting}
                      />
                      <label htmlFor="isActive" className="font-medium text-sm">
                        ສະຖານະການເປີດໃຊ້ງານ
                      </label>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      ກຳນົດວ່າຄູປອງນີ້ພ້ອມໃຊ້ງານທັນທີຫຼືບໍ່.
                    </p>
                  </div>
                )}
              />
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={submitting}
            >
              ຍົກເລີກການປ່ຽນແປງ
            </Button>
            <Button type="submit" disabled={submitting} className="ml-auto">
              {submitting ? "ກຳລັງບັນທຶກ..." : submitLabel}
            </Button>
          </div>
        </div>

        {/* Right Column: Preview */}
        <div className="space-y-6">
          {/* Coupon Summary Preview */}
          <Card className="gap-2 rounded-xl border border-primary/20 bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/20 dark:to-blue-900/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">
                  ສະຫຼຸບຄູປອງໃໝ່
                </CardTitle>
                <span className="rounded-full bg-blue-500/10 px-2 py-1 font-medium text-blue-700 text-xs dark:text-blue-400">
                  ຕົວຢ່າງ
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">ລະຫັດ:</span>
                  <span className="font-mono font-semibold text-blue-600 dark:text-blue-400">
                    {watchedValues.code || "–"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">
                    ຈຳກັດລວມ:
                  </span>
                  <span className="font-medium">
                    {watchedValues.maxRedemptions
                      ? watchedValues.maxRedemptions.toLocaleString("lo-LA")
                      : "ບໍ່ຈຳກັດ"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">
                    ຈຳກັດຜູ້ໃຊ້:
                  </span>
                  <span className="font-medium">
                    {watchedValues.perCustomerLimit
                      ? `${watchedValues.perCustomerLimit.toLocaleString("lo-LA")} ຕໍ່ລູກຄ້າ`
                      : "ບໍ່ຈຳກັດ"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">ສະຖານະ:</span>
                  <div className="flex items-center gap-2">
                    <div
                      className={`size-2 rounded-full ${
                        watchedValues.isActive ? "bg-green-500" : "bg-gray-400"
                      }`}
                    />
                    <span className="font-medium">
                      {watchedValues.isActive ? "ເປີດໃຊ້ງານ" : "ປິດໃຊ້ງານ"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Generated Visual */}
              {watchedValues.code && (
                <div className="mt-6 space-y-2">
                  <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                    ຮູບພາບທີ່ສ້າງຂຶ້ນ
                  </p>
                  <div className="flex items-center justify-center rounded-lg bg-blue-600 p-6">
                    <div className="flex items-center gap-3">
                      <TicketIcon className="h-8 w-8 text-white" />
                      <span className="font-bold font-mono text-2xl text-white">
                        {watchedValues.code}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Admin Tip */}
          <Card className="rounded-xl border border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
            <CardContent>
              <div className="flex items-start gap-3">
                <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
                <div className="space-y-1">
                  <h4 className="font-semibold text-blue-900 text-sm dark:text-blue-100">
                    ເຄັດລັບຜູ້ດູແລລະບົບ
                  </h4>
                  <p className="text-blue-700 text-xs leading-relaxed dark:text-blue-300">
                    ໃຊ້ລະຫັດສັ້ນໆ ທີ່ຈື່ງ່າຍສຳລັບການປ້ອນດ້ວຍຕົນເອງ. ສຳລັບອີເມວອັດຕະໂນມັດ,
                    ລະຫັດທີ່ຍາວແລະສຸ່ມຈະດີກວ່າສຳລັບຄວາມປອດໄພ.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </FormRoot>
  );
}
